import {
  PhSlidersHorizontal,
  PhUserCircle,
  PhMoney,
  PhClipboardText,
  PhBooks,
  PhUsersThree,
  PhPaperclip,
  PhSparkle,
  PhMagnifyingGlass,
  PhQuestion,
  PhBell,
  PhRocketLaunch,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

// The reimagined Program Builder is a single-page workspace navigated by a left
// rail of sections. This module is the section registry + the pure, type-driven
// logic that decides which sections apply, their status, and overall completion.
// It is intentionally framework-free (no tracking) so it can be unit-tested and
// reused by the rail, the action bar, and the (Phase 2) summary rail.
//
// Ported from the design prototype's `data.js` (SECTIONS / sectionStatus /
// completion), but it reads the *real* model's `fields` hash rather than a flat
// state object — almost every program setting lives inside `fields.<NAME>`.

type FieldsSignature = TASProgramTemplateModel['fields'];
// Matches the `@icon` arg shape expected by SectionPanel / the rail so the same
// Phosphor icon component flows through both without variance errors.
type IconComponent = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

export type SectionId =
  | 'basics'
  | 'eligibility'
  | 'limits'
  | 'application'
  | 'courses'
  | 'approvals'
  | 'documents'
  | 'decisioning'
  | 'audit'
  | 'instructions'
  | 'notifications'
  | 'review';

/** A section either applies (`true`) or is explicitly Not-Applicable (`'na'`). */
export type Applicability = true | 'na';
export type SectionStatus = 'done' | 'attention' | 'idle' | 'na';

export interface ProgramTypeMeta {
  key: string;
  label: string;
  short: string;
  usesApplication: boolean;
  usesCourses: boolean;
  trackingOnly: boolean;
  /** Type typically runs the two-step flow (course pre-approval → final
   * evidence). Informational only — the Courses Pre-Approval workflow's
   * Required toggle stays admin-editable because edge cases exist. */
  requiresPreApproval?: boolean;
}

// Program types keyed by the `TAS.ProgramType.*` enum stored in `fields.TYPE`.
// NOTE: `TAS.ProgramType.1` is the Tracking-Only program — it predates the type
// system but is functionally the same thing (confirmed with the team). There is
// no separate `TAS.TrackingOnly` enum value, so the prototype's split is dropped.
// Labels are the raw type identifiers ("Type 4.3" …) for now — the friendly
// names were ambiguous/mismatched. `requiresPreApproval` marks the types that
// typically run the two-step flow (course pre-approval → final evidence
// approval): per the team, that's 4.3 and 3, while 4.2 and 2 are one-step
// (final evidence only). It only drives the informational rule callout — the
// workflow's Required toggle stays editable because edge cases exist.
export const PROGRAM_TYPES: Record<string, ProgramTypeMeta> = {
  'TAS.ProgramType.4.3': {
    key: 'TAS.ProgramType.4.3',
    label: 'Type 4.3',
    short: 'Type 4.3',
    usesApplication: true,
    usesCourses: true,
    trackingOnly: false,
    requiresPreApproval: true,
  },
  'TAS.ProgramType.4.2': {
    key: 'TAS.ProgramType.4.2',
    label: 'Type 4.2',
    short: 'Type 4.2',
    usesApplication: true,
    usesCourses: true,
    trackingOnly: false,
  },
  'TAS.ProgramType.3': {
    key: 'TAS.ProgramType.3',
    label: 'Type 3',
    short: 'Type 3',
    usesApplication: true,
    usesCourses: true,
    trackingOnly: false,
    requiresPreApproval: true,
  },
  'TAS.ProgramType.2': {
    key: 'TAS.ProgramType.2',
    label: 'Type 2',
    short: 'Type 2',
    usesApplication: true,
    usesCourses: true,
    trackingOnly: false,
  },
  'TAS.ProgramType.1': {
    key: 'TAS.ProgramType.1',
    label: 'Type 1',
    short: 'Type 1',
    usesApplication: false,
    usesCourses: false,
    trackingOnly: true,
  },
};

// Fallback for an as-yet-unset or unknown type: treat as a full-featured program
// so every section is reachable rather than silently N/A.
const DEFAULT_TYPE_META: ProgramTypeMeta = {
  key: '',
  label: 'Program',
  short: 'Program',
  usesApplication: true,
  usesCourses: true,
  trackingOnly: false,
};

export function programTypeMeta(type: string | undefined | null): ProgramTypeMeta {
  return (type && PROGRAM_TYPES[type]) || DEFAULT_TYPE_META;
}

export interface SectionDef {
  id: SectionId;
  icon: IconComponent;
  /** i18n key for the rail label + section header (label === title for every
   * section, so one key serves both). Resolved by the builder via `intl`. */
  titleKey: string;
  /** i18n key for the section description. */
  descKey: string;
  /** Whether this section applies for the given program type. */
  applies: (meta: ProgramTypeMeta) => Applicability;
  /** TIO-internal config; never rendered on an employer-facing surface. Declared
   * here so a new internal section is private by default. */
  adminOnly?: boolean;
  /** Optional rail label / heading / description i18n keys for tracking-only
   * (Type 1) programs, where a reused section means something different. */
  trackingOnlyLabelKey?: string;
  trackingOnlyTitleKey?: string;
  trackingOnlyDescKey?: string;
}

export const SECTIONS: SectionDef[] = [
  {
    id: 'basics',
    icon: PhSlidersHorizontal,
    titleKey: 'program_builder.sections.basics.title',
    descKey: 'program_builder.sections.basics.desc',
    applies: () => true,
  },
  {
    id: 'eligibility',
    icon: PhUserCircle,
    titleKey: 'program_builder.sections.eligibility.title',
    descKey: 'program_builder.sections.eligibility.desc',
    applies: () => true,
  },
  {
    id: 'limits',
    icon: PhMoney,
    titleKey: 'program_builder.sections.limits.title',
    descKey: 'program_builder.sections.limits.desc',
    applies: (meta) => (meta.trackingOnly ? 'na' : true),
  },
  {
    id: 'application',
    icon: PhClipboardText,
    titleKey: 'program_builder.sections.application.title',
    descKey: 'program_builder.sections.application.desc',
    // Tracking-only programs have no application workflow, but still configure
    // their PROGRAM/EMPLOYEE-province fields here.
    applies: (meta) => (meta.usesApplication || meta.trackingOnly ? true : 'na'),
    trackingOnlyLabelKey: 'program_builder.sections.application.tracking_title',
    trackingOnlyTitleKey: 'program_builder.sections.application.tracking_title',
    trackingOnlyDescKey: 'program_builder.sections.application.tracking_desc',
  },
  {
    id: 'courses',
    icon: PhBooks,
    titleKey: 'program_builder.sections.courses.title',
    descKey: 'program_builder.sections.courses.desc',
    applies: (meta) => (meta.usesCourses ? true : 'na'),
  },
  {
    id: 'approvals',
    icon: PhUsersThree,
    titleKey: 'program_builder.sections.approvals.title',
    descKey: 'program_builder.sections.approvals.desc',
    applies: () => true,
  },
  {
    id: 'documents',
    icon: PhPaperclip,
    titleKey: 'program_builder.sections.documents.title',
    descKey: 'program_builder.sections.documents.desc',
    // Tracking-only programs still configure program-approval attachments here
    // (only that group renders for them — see section-documents).
    applies: (meta) => (meta.usesCourses || meta.trackingOnly ? true : 'na'),
    trackingOnlyTitleKey: 'program_builder.sections.documents.tracking_title',
    trackingOnlyDescKey: 'program_builder.sections.documents.tracking_desc',
  },
  {
    id: 'decisioning',
    icon: PhSparkle,
    titleKey: 'program_builder.sections.decisioning.title',
    descKey: 'program_builder.sections.decisioning.desc',
    applies: () => true,
    adminOnly: true,
  },
  {
    id: 'audit',
    icon: PhMagnifyingGlass,
    titleKey: 'program_builder.sections.audit.title',
    descKey: 'program_builder.sections.audit.desc',
    applies: () => true,
    adminOnly: true,
  },
  {
    id: 'instructions',
    icon: PhQuestion,
    titleKey: 'program_builder.sections.instructions.title',
    descKey: 'program_builder.sections.instructions.desc',
    applies: () => true,
  },
  {
    id: 'notifications',
    icon: PhBell,
    titleKey: 'program_builder.sections.notifications.title',
    descKey: 'program_builder.sections.notifications.desc',
    applies: (meta) => (meta.trackingOnly ? 'na' : true),
  },
  {
    id: 'review',
    icon: PhRocketLaunch,
    titleKey: 'program_builder.sections.review.title',
    descKey: 'program_builder.sections.review.desc',
    applies: () => true,
  },
];

export function sectionById(id: SectionId): SectionDef {
  return SECTIONS.find((s) => s.id === id) ?? SECTIONS[0]!;
}

/**
 * Read the first value of a field config from the (loosely typed) `fields` hash,
 * mirroring the model getters (`fields.NAME.values[0]`). Shared by the section
 * components, rules engine, and status logic.
 */
export function getFieldValue(fields: FieldsSignature | undefined, name: string): unknown {
  return (fields as Record<string, { values?: unknown[] } | undefined> | undefined)?.[name]
    ?.values?.[0];
}

/** Read the full values array of a multi-value field (e.g. passing grades). */
export function getFieldValues(fields: FieldsSignature | undefined, name: string): unknown[] {
  return (
    (fields as Record<string, { values?: unknown[] } | undefined> | undefined)?.[name]?.values ?? []
  );
}

// Per-section completion status. Phase 0 only scores the sections it can read
// cheaply from the model; the rest report `idle` until their own phases wire up
// real checks. Kept conservative so the meter never over-claims progress.
export function sectionStatus(
  fields: FieldsSignature | undefined,
  meta: ProgramTypeMeta,
  id: SectionId
): SectionStatus {
  const section = sectionById(id);
  if (section.applies(meta) === 'na') {
    return 'na';
  }

  switch (id) {
    case 'basics': {
      const hasType = Boolean(getFieldValue(fields, 'TYPE'));
      const hasName = Boolean(String(getFieldValue(fields, 'PROGRAM_NAME') ?? '').trim());
      if (hasType && hasName) return 'done';
      return hasType ? 'attention' : 'idle';
    }
    case 'decisioning':
    case 'audit':
      // Always valid — both are optional, every field having a default.
      return 'done';
    default:
      return 'idle';
  }
}

/** Percent of applicable, non-review sections currently in a `done` state. */
export function completion(fields: FieldsSignature | undefined, meta: ProgramTypeMeta): number {
  const applicable = SECTIONS.filter((s) => s.id !== 'review' && s.applies(meta) !== 'na');
  if (!applicable.length) return 0;
  const done = applicable.filter((s) => sectionStatus(fields, meta, s.id) === 'done').length;
  return Math.round((done / applicable.length) * 100);
}
